import React, { useState, useEffect, useRef } from 'react';
import './game.css';

const SnakeGame = ({ rows = 20, cols = 30 }) => {
  const [snake, setSnake] = useState([{ x: 0, y: 0 }]);
  const [direction, setDirection] = useState('right');
  const [food, setFood] = useState([]);
  const [gameOver, setGameOver] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [score, setScore] = useState(0);
  const [win, setWin] = useState(false);
  const gameRef = useRef(null);
  const gridSize = 10; // 每个格子的大小
  const totalFood = 10; // 需要吃掉的果子数量

  // 初始化游戏
  useEffect(() => {
    resetGame();
  }, [rows, cols]);

  // 游戏循环
  useEffect(() => {
    if (!gameStarted || gameOver || win) return;

    const interval = setInterval(() => {
      moveSnake();
    }, 100);

    return () => clearInterval(interval);
  }, [gameStarted, gameOver, win, snake, direction]);

  // 生成随机位置
  const generateRandomPosition = () => {
    return {
      x: Math.floor(Math.random() * cols),
      y: Math.floor(Math.random() * rows)
    };
  };

  // 检查位置是否被占用
  const isPositionOccupied = (position, excludeSnake = false) => {
    if (!excludeSnake && snake.some(segment => segment.x === position.x && segment.y === position.y)) {
      return true;
    }
    if (food.some(item => item.x === position.x && item.y === position.y)) {
      return true;
    }
    return false;
  };

  // 生成食物
  const generateFood = () => {
    const newFood = [];
    while (newFood.length < totalFood) {
      const position = generateRandomPosition();
      if (!isPositionOccupied(position)) {
        newFood.push(position);
      }
    }
    setFood(newFood);
  };

  // 重置游戏
  const resetGame = () => {
    setSnake([{ x: 0, y: 0 }]);
    setDirection('right');
    generateFood();
    setGameOver(false);
    setGameStarted(false);
    setScore(0);
    setWin(false);
  };

  // 开始游戏
  const startGame = () => {
    setGameStarted(true);
  };

  // 移动蛇
  const moveSnake = () => {
    setSnake(prevSnake => {
      const head = prevSnake[0];
      let newHead;

      // 根据方向计算新的头部位置
      switch (direction) {
        case 'up':
          newHead = { x: head.x, y: head.y - 1 };
          break;
        case 'down':
          newHead = { x: head.x, y: head.y + 1 };
          break;
        case 'left':
          newHead = { x: head.x - 1, y: head.y };
          break;
        case 'right':
          newHead = { x: head.x + 1, y: head.y };
          break;
        default:
          newHead = head;
      }

      // 检查是否撞墙
      if (newHead.x < 0 || newHead.x >= cols || newHead.y < 0 || newHead.y >= rows) {
        setGameOver(true);
        setGameStarted(false);
        return prevSnake;
      }

      // 检查是否撞到自己
      if (prevSnake.some(segment => segment.x === newHead.x && segment.y === newHead.y)) {
        setGameOver(true);
        setGameStarted(false);
        return prevSnake;
      }

      // 检查是否吃到食物
      const newFood = food.filter(item => !(item.x === newHead.x && item.y === newHead.y));
      if (newFood.length < food.length) {
        setFood(newFood);
        setScore(prevScore => prevScore + 1);
        
        // 检查是否胜利
        if (newFood.length === 0) {
          setWin(true);
          setGameStarted(false);
        }
        
        // 吃到食物，蛇变长
        return [newHead, ...prevSnake];
      } else {
        // 没有吃到食物，蛇移动
        return [newHead, ...prevSnake.slice(0, -1)];
      }
    });
  };

  // 处理键盘事件
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!gameStarted) return;

      switch (e.key) {
        case 'ArrowUp':
          if (direction !== 'down') setDirection('up');
          break;
        case 'ArrowDown':
          if (direction !== 'up') setDirection('down');
          break;
        case 'ArrowLeft':
          if (direction !== 'right') setDirection('left');
          break;
        case 'ArrowRight':
          if (direction !== 'left') setDirection('right');
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [direction, gameStarted]);

  return (
    <div 
      className="snake-game-container"
      onClick={startGame}
      ref={gameRef}
      style={{
        width: '100%',
        maxWidth: `${cols * gridSize}px`,
        height: `${rows * gridSize}px`,
        border: '1px solid #ccc',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#eee',
        marginTop: '20px',
        marginBottom: '20px'
      }}
    >
      {/* 蛇身 */}
      {snake.map((segment, index) => (
        <div
          key={index}
          className={`snake-segment ${index === 0 ? 'snake-head' : ''}`}
          style={{
            position: 'absolute',
            width: `${gridSize}px`,
            height: `${gridSize}px`,
            left: `${segment.x * gridSize}px`,
            top: `${segment.y * gridSize}px`,
            backgroundColor: index === 0 ? '#333' : '#666',
            borderRadius: index === 0 ? '30%' : '0'
          }}
        />
      ))}

      {/* 食物 */}
      {food.map((item, index) => (
        <div
          key={index}
          className="food"
          style={{
            position: 'absolute',
            width: `${gridSize}px`,
            height: `${gridSize}px`,
            left: `${item.x * gridSize}px`,
            top: `${item.y * gridSize}px`,
            backgroundColor: '#ff6b6b',
            borderRadius: '50%'
          }}
        />
      ))}

      {/* 游戏状态 */}
      {!gameStarted && !gameOver && !win && (
        <div className="game-overlay">
          <div className="game-message">try try ??</div>
        </div>
      )}

      {gameOver && (
        <div className="game-overlay">
          <div className="game-message">游戏结束</div>
          <div className="game-score">得分: {score}</div>
          <button onClick={resetGame} className="game-button">重新开始</button>
        </div>
      )}

      {win && (
        <div className="game-overlay">
          <div className="game-message">恭喜你，胜利了！</div>
          <div className="game-score">得分: {score}</div>
          <button onClick={resetGame} className="game-button">重新开始</button>
        </div>
      )}
    </div>
  );
};

export default SnakeGame;